import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader } from 'rsuite';
import Layout from '../Layout';
import { AssignedUsersCard, PriceApplicationModeField, SelectUsersModal } from './components';
import { DEFAULT_PRICE_APPLICATION_CONFIG } from './constants';
import { usePriceApplicationConfigQuery, useSavePriceApplicationConfig } from './hooks';
import { PricingPermissionFormValues, pricingPermissionFormSchema } from './schema';
import { PermittedUser } from './types';
import styles from './SetupIpaPricingPermissions.module.scss';

const { Section, Footer, Subtitle, Title, Action } = Layout;

// Modal e "Remover" só mexem no rascunho do form; o salvar do rodapé é o único que grava.
// O <form> envolve só o radio: o submit do modal borbulha pelo portal na árvore do React e salvaria a tela.
const MODE_FORM_ID = 'pricing-permission-mode-form';

const SetupIpaPricingPermissions = () => {
  const configQuery = usePriceApplicationConfigQuery();
  const saveConfig = useSavePriceApplicationConfig();

  const [isSelectUsersOpen, setIsSelectUsersOpen] = useState(false);

  const config = configQuery.data ?? DEFAULT_PRICE_APPLICATION_CONFIG;

  const form = useForm<PricingPermissionFormValues>({
    resolver: zodResolver(pricingPermissionFormSchema),
    values: { mode: config.mode, users: config.users },
  });

  const isSpecificUsers = form.watch('mode') === 'SPECIFIC_USERS';
  const draftUsers = form.watch('users');
  const isSavingConfig = saveConfig.isLoading;
  // Sem a config salva, a tela mostraria o padrão e salvar sobrescreveria uma restrição existente.
  const isConfigUnavailable = configQuery.isError;

  const handleSubmit = form.handleSubmit(values => saveConfig.mutate(values));

  const handleReset = () => form.reset();

  const setDraftUsers = (users: PermittedUser[]) => form.setValue('users', users, { shouldDirty: true });

  const handleSaveUsers = (users: PermittedUser[]) => {
    setDraftUsers(users);
    setIsSelectUsersOpen(false);
  };

  const handleRemoveUser = (user: PermittedUser) => setDraftUsers(draftUsers.filter(item => item.cd_usuario !== user.cd_usuario));

  if (configQuery.isLoading) {
    return (
      <div className={styles.loading}>
        <Loader content="Carregando permissões..." />
      </div>
    );
  }

  return (
    <Layout.Root asChild>
      <div>
        <Section.Root>
          <Section.Header>
            <Title>Permissões de precificação</Title>
            <Subtitle>
              Defina quem pode executar cada uma das ações abaixo. As permissões são independentes entre si e valem para todos os usuários do IPA.
            </Subtitle>
          </Section.Header>
        </Section.Root>

        <Section.Root className={styles.block}>
          <Section.Header>
            <Title>Aplicação de preços no Gerenciador</Title>
            <Subtitle>Defina quem pode aplicar preços aos produtos no Gerenciador de Preços.</Subtitle>
          </Section.Header>
          <Section.Content className={styles.content}>
            <form id={MODE_FORM_ID} onSubmit={handleSubmit} onReset={handleReset}>
              <PriceApplicationModeField control={form.control} disabled={isConfigUnavailable} />
            </form>
            {isSpecificUsers && (
              <AssignedUsersCard users={draftUsers} busy={isSavingConfig} onAdd={() => setIsSelectUsersOpen(true)} onRemove={handleRemoveUser} />
            )}
          </Section.Content>
        </Section.Root>

        <Footer>
          <Action theme="ghost" type="reset" form={MODE_FORM_ID}>
            cancelar
          </Action>
          <Action type="submit" form={MODE_FORM_ID} isLoading={isSavingConfig} disabled={isSavingConfig || isConfigUnavailable}>
            salvar
          </Action>
        </Footer>

        {isSelectUsersOpen && (
          <SelectUsersModal
            selectedUsers={draftUsers}
            onClose={() => setIsSelectUsersOpen(false)}
            onSave={handleSaveUsers}
          />
        )}
      </div>
    </Layout.Root>
  );
};

export { SetupIpaPricingPermissions };
